import {
  ActionIcon,
  Button,
  CloseButton,
  Group,
  LoadingOverlay,
  Modal,
  Paper,
  Stack,
  Text,
  TextInput,
  Title,
  Tooltip,
} from "@mantine/core";
import { useEffect, useMemo, useState } from "react";
import {
  editLinkedinInvitation,
  editLinkedinMessage,
  editMEmail,
  editSMS,
  findLinkedinInvitation,
  findLinkedinMessage,
  findMEmail,
  getLinkedinInvitations,
  getLinkedinMessages,
  getMEmails,
} from "../../../api/ModelsApi";
import {
  IconBook2,
  IconCopy,
  IconPin,
  IconPinnedOff,
  IconSearch,
  IconTemplate,
} from "@tabler/icons-react";
import EmailPreviewer from "../../../components/View/EmailPreviewer";
import { useLanguage } from "../../../stores/LanguageStore";
import BackButton from "../../../components/View/BackButton";
import MessagePreviewer from "../../../components/View/MessagePreviewer";
import { useLeadsSheetStore } from "../../../stores/LeadsStore";
import { searchNormalize } from "../../../functions/utilFunctions";

const ModelTemplateSender = ({ onImport, type }) => {
  const lang = useLanguage((s) => s.language);
  const [opened, setOpened] = useState(false);
  const [initializing, setInitializing] = useState(true);
  const current = useLeadsSheetStore((s) => s.current);

  const [templateItems, setTemplateItems] = useState([]);

  const handleOpen = () => {
    setOpened(true);
  };
  const handleClose = () => {
    setOpened(false);
  };

  const [selected, setSelected] = useState(null);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [finding, setFinding] = useState(false);

  const handleImport = () => {
    onImport(selectedProfile);
    setOpened(false);
  };

  useEffect(() => {
    let func;
    if (type === "email") {
      func = getMEmails;
    }
    if (type === "linkedin_invitation") {
      func = getLinkedinInvitations;
    }
    if (type === "linkedin_message") {
      func = getLinkedinMessages;
    }

    func()
      .then((r) => {
        setTemplateItems(r.data.models_list);
        setSelected(r.data.models_list[0]);
      })
      .finally(() => setInitializing(false));
  }, [type]);

  useEffect(() => {
    if (selected) {
      let func;
      if (type === "email") {
        func = findMEmail;
      }
      if (type === "linkedin_invitation") {
        func = findLinkedinInvitation;
      }
      if (type === "linkedin_message") {
        func = findLinkedinMessage;
      }

      setFinding(true);
      func(selected.id)
        .then((r) => {
          setSelectedProfile(r.data.model_profile);
        })
        .finally(() => setFinding(false));
    }
  }, [selected, type]);

  return (
    <>
      <Button
        leftSection={<IconTemplate size={18} />}
        size="md"
        radius={"md"}
        onClick={handleOpen}
        variant="light"
      >
        Modèles
      </Button>

      <Modal
        opened={opened}
        onClose={handleClose}
        title={<Title order={5}>Vos modèles</Title>}
        size={"80%"}
      >
        <Group
          h={"70vh"}
          w={"100%"}
          wrap="nowrap"
          pos={"relative"}
          align="start"
        >
          <LoadingOverlay visible={initializing} />
          <LeftSide
            items={templateItems}
            setItems={setTemplateItems}
            selected={selected}
            setSelected={setSelected}
            type={type}
          />
          <Stack w={"70%"} h={"100%"} pos={"relative"}>
            <LoadingOverlay visible={finding} />
            <Paper w={"100%"} h={"100%"}>
              {type === "email" ? (
                <EmailPreviewer
                  key={selectedProfile?.id}
                  html={selectedProfile?.html}
                  subject={selectedProfile?.subject}
                  iframeHeight={"calc(70vh - 96px)"}
                  contact={current}
                />
              ) : (
                <MessagePreviewer
                  key={selectedProfile?.id}
                  text={selectedProfile?.text}
                  file_id={selectedProfile?.file_id}
                  type={type}
                  contact={current}
                />
              )}
            </Paper>
            <Group justify="end">
              <BackButton onClick={handleClose} size="md" />
              <Button
                leftSection={<IconCopy size={18} />}
                size="md"
                onClick={handleImport}
              >
                {lang.model_emails.templates.duplicate}
              </Button>
            </Group>
          </Stack>
        </Group>
      </Modal>
    </>
  );
};

const LeftSide = ({ items, setItems, selected, setSelected, type }) => {
  const lang = useLanguage((s) => s.language);
  const [search, setSearch] = useState("");
  const [pinning, setPinning] = useState(null);

  const searchItems = useMemo(() => {
    if (search) {
      return items.filter((item) => {
        return searchNormalize(item.name).includes(searchNormalize(search));
      });
    }
    return items;
  }, [items, search]);

  const handlePin = (item) => {
    setPinning(item.id);

    let main;
    if (type === "linkedin_invitation") {
      main = editLinkedinInvitation
    }
    if (type === "linkedin_message") {
      main = editLinkedinMessage
    }
    if (type === "sms") {
      main = editSMS
    }
    if (type === "email") {
      main = editMEmail
    }
    main({
      id: item.id,
      pin: !item?.pin,
    })
      .then((r) => {
        setItems(
          items.map((x) => ({
            ...x,
            pin: item.id === x.id ? !item.pin : x.pin,
          }))
        );
      })
      .finally(() => setPinning(null));
  };

  return (
    <Paper
      component={Stack}
      h={"100%"}
      style={{ overflow: "auto" }}
      w={"30%"}
      gap={"sm"}
      pb={"lg"}
      px={"sm"}
    >
      <TextInput
        placeholder="Rechercher..."
        size="md"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        leftSection={<IconSearch size={16} />}
        rightSection={search && <CloseButton onClick={() => setSearch("")} />}
      />
      {searchItems
        ?.sort((a, b) => b.pin - a.pin)
        ?.map((item) => (
          <Group key={item.id}>
            <Tooltip label={item.name} key={item.id}>
              <Button
                variant={selected?.id === item.id ? "filled" : "outline"}
                onClick={() => setSelected(item)}
                mih={48}
                justify="start"
                flex={1}
              >
                <Text truncate="end" fw={500} size="sm">
                  {item.name}
                </Text>
              </Button>
            </Tooltip>
            <Tooltip
              label={lang.table.toolbar.filter[item?.pin ? "unpin" : "pin"]}
            >
              <ActionIcon
                onClick={() => handlePin(item)}
                loading={pinning === item.id}
                disabled={pinning}
                variant={item?.pin ? "filled" : "subtle"}
                size={"lg"}
              >
                <IconPin size={18} />
              </ActionIcon>
            </Tooltip>
          </Group>
        ))}
    </Paper>
  );
};

export default ModelTemplateSender;
